import { useEffect } from "react";

/**
 * usePageMeta — keeps title / description / canonical / og:url in sync per route.
 *
 * The base meta tags live in index.html (good for SSR / link previews on first hit).
 * This hook updates them client-side as the user navigates between routes so each
 * page presents a unique title for browser tabs, social shares, and bookmarks.
 */
export default function usePageMeta({ title, description, path = "/", image } = {}) {
  useEffect(() => {
    if (title) document.title = title;

    // Find an existing tag OR create it. Without this fallback, a route that
    // mounts BEFORE index.html's tags load (or one served from a pre-rendered
    // route file that lacks a tag) would silently skip the update.
    const ensure = (selector, factory) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = factory();
        document.head.appendChild(el);
      }
      return el;
    };

    const origin = "https://aipresidential.com";
    const fullUrl = origin + (path.startsWith("/") ? path : `/${path}`);

    if (description) {
      const m = ensure('meta[name="description"]', () => {
        const e = document.createElement("meta"); e.setAttribute("name", "description"); return e;
      });
      m.setAttribute("content", description);
    }

    const canon = ensure('link[rel="canonical"]', () => {
      const e = document.createElement("link"); e.setAttribute("rel", "canonical"); return e;
    });
    canon.setAttribute("href", fullUrl);

    const ogUrl = ensure('meta[property="og:url"]', () => {
      const e = document.createElement("meta"); e.setAttribute("property", "og:url"); return e;
    });
    ogUrl.setAttribute("content", fullUrl);

    if (title) {
      const ogT = ensure('meta[property="og:title"]', () => {
        const e = document.createElement("meta"); e.setAttribute("property", "og:title"); return e;
      });
      ogT.setAttribute("content", title);
      const twT = ensure('meta[name="twitter:title"]', () => {
        const e = document.createElement("meta"); e.setAttribute("name", "twitter:title"); return e;
      });
      twT.setAttribute("content", title);
    }
    if (description) {
      const ogD = ensure('meta[property="og:description"]', () => {
        const e = document.createElement("meta"); e.setAttribute("property", "og:description"); return e;
      });
      ogD.setAttribute("content", description);
      const twD = ensure('meta[name="twitter:description"]', () => {
        const e = document.createElement("meta"); e.setAttribute("name", "twitter:description"); return e;
      });
      twD.setAttribute("content", description);
    }
    if (image) {
      const ogI = ensure('meta[property="og:image"]', () => {
        const e = document.createElement("meta"); e.setAttribute("property", "og:image"); return e;
      });
      ogI.setAttribute("content", image);
      const twI = ensure('meta[name="twitter:image"]', () => {
        const e = document.createElement("meta"); e.setAttribute("name", "twitter:image"); return e;
      });
      twI.setAttribute("content", image);
    }
  }, [title, description, path, image]);
}
